import React from 'react';
import { useFullscreenOverlay } from './FullscreenOverlayContext';
import {
  header,
  headerColumns,
  headerColumn1,
  headerColumn2,
  headerColumn3,
  headerTitle,
  closeButton,
  floatingCloseButton,
  mediaCloseButtonInline,
} from './fullscreenOverlay.styles';
import { useScrollFadeHeader } from '../../../pages/HaloListingPage/hooks/useScrollFadeHeader';
import { CloseButton } from '../CloseButton';
import type { FullscreenOverlayHeaderProps } from './types';
import type { CSSProperties } from 'react';

/**
 * FullscreenOverlayHeader Component
 *
 * Header section with flexible layout options:
 * - Three-column layout (close | title | actions)
 * - Sticky or floating positioning
 * - Variant-specific styling
 *
 * @example Basic header
 * <FullscreenOverlay.Header
 *   title="Amenities"
 *   actions={<ShareButton />}
 * />
 *
 * @example Floating header for media variant
 * <FullscreenOverlay.Header
 *   layout="floating"
 *   variant="media"
 *   showCloseButton={false}
 * />
 */
export const FullscreenOverlayHeader = ({
  title,
  actions,
  showCloseButton = true,
  layout = 'sticky',
  variant = 'standard',
  scrollContainerRef,
}: FullscreenOverlayHeaderProps) => {
  const { close } = useFullscreenOverlay();

  const shouldApplyScrollFade = false;
  const { backgroundOpacity } = useScrollFadeHeader({
    enabled: shouldApplyScrollFade,
    scrollContainerRef,
  });

  const handleCloseClick = () => {
    close();
  };

  // Dynamic styles for scroll fade effect
  const dynamicHeaderStyles: CSSProperties = shouldApplyScrollFade
    ? {
        backgroundColor: `rgba(255, 255, 255, ${backgroundOpacity})`,
        borderBottomColor: backgroundOpacity > 0.8 ? '#d8d8d8' : 'transparent',
        borderBottomWidth: backgroundOpacity > 0.8 ? '1px' : '0px',
        borderBottomStyle: 'solid' as const,
        transition: 'background-color 0.2s ease-out, border-bottom 0.2s ease-out',
      }
    : {};

  // For media variant with floating layout, use different positioning
  if (variant === 'media' && layout === 'floating') {
    if (actions) {
      return (
        <header className={header({ layout: 'floating', variant: 'media' })}>
          <div className={headerColumns}>
            <div className={headerColumn1}>
              <CloseButton onClick={handleCloseClick} aria-label="Close overlay" className={mediaCloseButtonInline} />
            </div>
            <div className={headerColumn2} />
            <div className={headerColumn3}>{actions}</div>
          </div>
        </header>
      );
    }
    return <CloseButton variant="floating" onClick={handleCloseClick} aria-label="Close overlay" />;
  }

  return (
    <header className={header({ layout, variant })} style={dynamicHeaderStyles}>
      <div className={headerColumns}>
        {/* Column 1: Close button */}
        <div className={headerColumn1}>
          {showCloseButton && <CloseButton onClick={handleCloseClick} aria-label="Close overlay" />}
        </div>

        {/* Column 2: Title (centered) */}
        <div className={headerColumn2}>
          {title && (
            <h2 id="overlay-title" className={headerTitle}>
              {title}
            </h2>
          )}
        </div>

        {/* Column 3: Action buttons */}
        <div className={headerColumn3}>{actions}</div>
      </div>
    </header>
  );
};
